import { NotificationDataSourceEnum } from '@inter-ikea-kompis/enums';
import type { NotificationLinkTypeEnum } from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';

import DexfNotificationService from '../../services/notification/DexfNotificationService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
import type INotificationServiceOptions from './INotificationServiceOptions.js';

/**
 * This class is used as an interface for Dexf notification service.
 */
export default class NotificationService {
	private readonly dexfConfiguration: DexfConfiguration;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 */
	constructor(options: INotificationServiceOptions) {
		this.dexfConfiguration = new DexfConfiguration(options);
	}

	/**
	 * Send planner configuration to user via Email.
	 *
	 * @param options Notification Options.
	 * @param options.vpcCode Vpc code.
	 * @param options.email Email address.
	 * @param options.linkType Link type.
	 * @param options.storeId Store ID. Configurable for pickinglist.
	 * @returns True if sent.
	 */
	public async sendEmail(options: {
		vpcCode: string;
		email: string;
		linkType: NotificationLinkTypeEnum;
		storeId?: string;
	}): Promise<void> {
		const service = await this.getService();
		const { vpcCode, email, linkType, storeId } = options;
		return await service.sendNotification({
			vpcCode,
			emailAddress: email,
			linkType,
			storeId
		});
	}

	/**
	 * Returns service.
	 *
	 * @returns Service.
	 */
	private async getService(): Promise<DexfNotificationService> {
		const { kompis } = await this.dexfConfiguration.getSettings();
		const dexfApiKey = await this.dexfConfiguration.getDexfApiKeyOrThrow();

		switch (kompis.serviceSettings.notificationDataSource) {
			case NotificationDataSourceEnum.disabled:
				throw new ServiceException(
					`The notificationDataSource is set to "Disabled". When it is, the notification functionality should be disabled in the planner.`
				);
			default:
				return new DexfNotificationService({
					endpoint: kompis.endpoints.dexfNotificationService,
					dexfApiKey: dexfApiKey
				});
		}
	}
}
